import * as Sentry from '@sentry/nuxt'
import { useRuntimeConfig, useRouter } from '#imports'
import { getRouteNameWithoutLanguage } from '~/utils/getRouteNameWithoutLanguage'

const router = useRouter()
const environment = useRuntimeConfig().public.appEnv

const getTraceSampleRate = () => {
  switch (environment) {
    case 'production':
      return 0.2 // 20% sample rate for production
    case 'homolog':
      return 1.0 // 100% sample rate for homolog/staging
    case 'development':
    default:
      return 0 // Disable Sentry in development
  }
}

Sentry.init({
  // If set up, you can use your runtime config here
  dsn: useRuntimeConfig().public.sentry.dsn,
  environment, // Set environment for Sentry dashboard filtering
  ignoreErrors: [
    'Non-Error exception captured',
    'Non-Error promise rejection captured',
    // Network errors that are not actionable
    'NetworkError',
    'ERR_BLOCKED_BY_CLIENT',
    'ERR_NETWORK',
    // Ad blocker related errors
    'blocked by client',
    'blocked by extension',
    // Vue/Nuxt hydration warnings (not critical errors)
    'Hydration completed but contains mismatches',
    'Hydration node mismatch',
    'Hydration text mismatch',
    'hydration mismatch',
  ],

  integrations: [
    Sentry.browserTracingIntegration({ router }),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
      maskAllInputs: true,
    }),
    // Capture console errors only
    Sentry.captureConsoleIntegration({
      levels: ['error'],
    }),
  ],

  tracePropagationTargets: [
    'localhost',
    'https://gokonfetti.com',
    'https://front-hml.gokonfetti.com',
  ],
  sampleRate: getTraceSampleRate(),
  // Enable debug mode for better error tracking
  debug: environment !== 'production',
  // Increase replay sample rate for errors
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 0.01, // 0.01 (1% of errors will have replay)

  // Handle transport errors gracefully
  beforeSend(event, hint) {
    // Log transport errors to console in development
    if (environment !== 'production' && hint.originalException) {
      const error = hint.originalException
      if (error instanceof Error && error.message.includes('Failed to fetch')) {
        // eslint-disable-next-line no-console
        console.warn(
          'Sentry transport blocked (likely by ad blocker or firewall):',
          error.message
        )
        return null // Don't send the event
      }
    }

    // add routeName for grouping errors
    if (typeof window !== 'undefined' && router) {
      const currentRoute = router.currentRoute.value
      const routeNameWithoutLanguage = getRouteNameWithoutLanguage(
        currentRoute.name as string
      )
      // add routeName to tags for filtering
      event.tags = {
        ...event.tags,
        routeName: routeNameWithoutLanguage,
        fullRouteName: currentRoute.name as string,
      }

      // update fingerprint for grouping by routeName instead of fileName
      if (event.fingerprint) {
        // replace fileName in fingerprint with routeName
        event.fingerprint = event.fingerprint.map((fingerprint) => {
          // if fingerprint contains fileName (usually chunk name), replace with routeName
          if (
            (typeof fingerprint === 'string' &&
              fingerprint.includes('chunk')) ||
            fingerprint.includes('.js')
          ) {
            return routeNameWithoutLanguage
          }
          return fingerprint
        })
      } else {
        // create new fingerprint based on routeName
        event.fingerprint = [
          'error',
          routeNameWithoutLanguage,
          event.exception?.values?.[0]?.type || 'unknown',
          event.message?.substring(0, 50) || 'unknown',
        ]
      }
    }

    return event
  },

  // Use default transport (debug mode disabled for clean console)
  transport: undefined,
})

// Suppress Sentry transport error logs when Sentry is disabled (development)
if (typeof window !== 'undefined' && environment === 'development') {
  // Store original console methods
  // eslint-disable-next-line no-console
  const originalError = console.error
  // eslint-disable-next-line no-console
  const originalLog = console.log

  // Flag to show warning only once
  let sentryWarningShown = false

  // Override console.error to filter Sentry transport errors
  // eslint-disable-next-line no-console
  console.error = (...args: any[]) => {
    const message = String(args[0] || '')

    // Check if this is a Sentry transport error
    if (
      message.includes('Sentry Logger') &&
      (message.includes('Failed to fetch') ||
        message.includes('transport request') ||
        message.includes('Error while sending envelope'))
    ) {
      // Show user-friendly warning only once
      if (!sentryWarningShown) {
        originalError(
          '%cSentry error reporting is blocked (likely by ad blocker or firewall)',
          'color: orange; font-weight: bold;'
        )
        originalError(
          '%cYour app will continue to work normally. This is not an error in your code.',
          'color: orange;'
        )
        sentryWarningShown = true
      }
      return // Don't show the technical error
    }

    // Show all other errors normally
    originalError(...args)
  }

  // Override console.log to filter Sentry debug messages
  // eslint-disable-next-line no-console
  console.log = (...args: any[]) => {
    const message = String(args[0] || '')

    // Filter out Sentry debug messages
    if (
      message.includes('Sentry Logger') &&
      (message.includes('Flushing outcomes') ||
        message.includes('Sending outcomes') ||
        message.includes('Recording outcome'))
    ) {
      return // Don't show these debug messages
    }

    // Show all other logs normally
    originalLog(...args)
  }
}
